import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext'; // Keep useAuth import
import { User } from '../../types/user'; // Import User type from the new file
import api from '../../services/api'; // Import api service
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { checkConnectionStatus, ConnectionStatus, sendConnectionRequest } from '../../services/userService';
import { Alert, Button, Modal, Form } from 'react-bootstrap'; // Import Alert, Button, Modal, Form
import { showToast } from '../../components/common/ToastContainer';
import { findOrCreateConversation } from '../../services/messageService'; // Import message service

// Import components
import ProfileHeader from '../../components/UserProfile/ProfileHeader';
import ActivityTimeline from '../../components/UserProfile/ActivityTimeline';
import ProfessionalDetails from '../../components/UserProfile/ProfessionalDetails';
import AchievementBadges from '../../components/UserProfile/AchievementBadges';
import SkillsExpertise from '../../components/UserProfile/SkillsExpertise';
import NetworkingPreferences from '../../components/UserProfile/NetworkingPreferences';
import LocationMap from '../../components/UserProfile/LocationMap';
import ConnectionsList from '../../components/UserProfile/ConnectionsList';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// Define the structure matching the backend's GET /api/users/:id response
interface UserProfileData {
    id: number;
    name: string;
    profession?: string;
    experience?: string;
    company?: string;
    work_type?: string;
    linkedin?: string;
    country?: string;
    city?: string;
    latitude?: number | null;
    longitude?: number | null;
    skills?: string[];
    createdAt?: string;
    updatedAt?: string;
    location_name?: string;
    connectionsCount?: number;
    email?: string;
}

// API Response Types
interface UserProfileApiResponse {
    success: boolean;
    data?: UserProfileData;
    message?: string;
}

const UserProfilePage: React.FC = () => {
    const { userId: userIdFromParams } = useParams<{ userId: string }>();
    const { user: loggedInUser, isLoading: isLoadingAuth, isAuthenticated } = useAuth();
    const queryClient = useQueryClient();
    const navigate = useNavigate(); // Initialize navigate

    // State for the profile being viewed (fetched if userIdFromParams exists)
    const [viewedProfile, setViewedProfile] = useState<UserProfileData | null>(null);
    const [isLoadingViewedProfile, setIsLoadingViewedProfile] = useState<boolean>(false);
    const [viewedProfileError, setViewedProfileError] = useState<string | null>(null);

    // Convert param to number once
    const profileUserId = userIdFromParams ? parseInt(userIdFromParams, 10) : null;

    const isViewingOwnProfile = !profileUserId || (loggedInUser && loggedInUser.id === profileUserId);

    // --- Fetch Connection Status --- //
    const { data: connectionStatus, isLoading: isLoadingConnectionStatus } = 
        useQuery<ConnectionStatus, Error>(
        // Query key includes both user IDs
        ['connectionStatus', loggedInUser?.id, profileUserId], 
        () => checkConnectionStatus(profileUserId!), // Assert profileUserId is non-null here as it's enabled below
        {
            // Only run query if logged in, viewing another valid profile, and IDs differ
            enabled: isAuthenticated && !!profileUserId && !!loggedInUser && loggedInUser.id !== profileUserId,
            staleTime: 5 * 60 * 1000, // 5 minutes
            refetchOnWindowFocus: false, 
        }
    );

    useEffect(() => {
        // Fetch profile only if userId is present in URL params
        if (userIdFromParams) {
            const fetchUserProfile = async () => {
                setIsLoadingViewedProfile(true);
                setViewedProfileError(null);
                try {
                    // Use relative path, assuming api base URL includes /api
                    const response = await api.get<UserProfileApiResponse>(`/users/${userIdFromParams}`);
                    if (response.data.success && response.data.data) {
                        setViewedProfile(response.data.data);
                    } else {
                        throw new Error(response.data.message || 'Failed to fetch user profile.');
                    }
                } catch (err: any) {
                    console.error("Error fetching user profile:", err);
                    setViewedProfileError(err.response?.data?.message || err.message || 'Could not load profile.');
                } finally {
                    setIsLoadingViewedProfile(false);
                }
            };
            fetchUserProfile();
        } else {
            // Clear viewed profile state if navigating to own profile via /profile
            setViewedProfile(null);
            setIsLoadingViewedProfile(false);
            setViewedProfileError(null);
        }
    }, [userIdFromParams]); // Re-run if the userId param changes

    // Determine which profile data and loading/error state to use
    // Use a more specific type that matches the AuthContext user if viewing self
    let profileToDisplay: UserProfileData | User | null = null; 
    let isLoading: boolean = false;
    let displayError: string | null = null;

    if (userIdFromParams) {
        // Viewing someone else's profile (or own via ID)
        profileToDisplay = viewedProfile; // This is UserProfileData | null
        isLoading = isLoadingViewedProfile;
        displayError = viewedProfileError;
    } else {
        // Viewing own profile via /profile URL
        // Use data from Auth context (which should satisfy the stricter User type)
        profileToDisplay = loggedInUser; 
        isLoading = isLoadingAuth;
        displayError = null; // Assuming auth context handles its own errors differently
    }

    // --- Connection Modal State --- //
    const [showConnectionModal, setShowConnectionModal] = useState(false);
    // Use viewedProfile for recipient name initially, fallback if needed
    const [connectionRecipient, setConnectionRecipient] = useState<{ id: number; name: string }>({ id: 0, name: '' }); 
    const [connectionNote, setConnectionNote] = useState('');
    const [isSubmittingConnection, setIsSubmittingConnection] = useState(false);

    // --- Connection Request Mutation --- //
    const { 
        mutate: submitConnectionRequestMutate, 
        isLoading: isSendingConnection 
      } = useMutation( 
        (variables: { targetUserId: number, note?: string }) => 
          sendConnectionRequest(variables.targetUserId, variables.note),
        {
          onMutate: () => {
            setIsSubmittingConnection(true);
          },
          onSuccess: (data, variables) => {
            if (data.success) {
              setShowConnectionModal(false);
              setConnectionNote('');
              showToast(data.message || `Connection request sent to ${connectionRecipient.name}`, 'success');
              // Invalidate connection status query to update button state
              queryClient.invalidateQueries(['connectionStatus', loggedInUser?.id, profileUserId]);
            } else {
              showToast(data.message || 'Failed to send connection request', 'danger');
            }
          },
          onError: (error: any) => {
            console.error('Error sending connection request mutation:', error);
            let errorMessage = 'Failed to send connection request. Please try again.';
            if (error.isAxiosError && error.response?.status === 409) {
              errorMessage = error.response?.data?.message || 'Connection request already sent or users already connected.';
              showToast(errorMessage, 'warning'); 
            } else {
              errorMessage = error.message || errorMessage;
              showToast(errorMessage, 'danger');
            }
          },
          onSettled: () => {
            setIsSubmittingConnection(false);
          }
        }
      );
    
      // --- Submit Connection Request Form Handler --- //
      const submitConnectionRequest = () => {
        if (!connectionRecipient.id) return;
        submitConnectionRequestMutate({
          targetUserId: connectionRecipient.id,
          note: connectionNote,
        });
      };

    // --- Initiate Message Handler --- //
    const handleInitiateMessage = async (targetUserId: number, targetUserName?: string) => {
      if (!isAuthenticated || !loggedInUser) {
        showToast('Please log in to send messages.', 'danger');
        return;
      }
      // This check might be redundant if button is hidden on own profile, but good for safety
      if (loggedInUser.id === targetUserId) { 
        showToast('You cannot message yourself.', 'warning');
        return;
      }

      showToast(`Starting conversation with ${targetUserName || 'user'}...`, 'info');
      try {
        const conversationId = await findOrCreateConversation(targetUserId);
        if (conversationId) {
          // Use a more general key or one that matches your ConversationList query key
          await queryClient.invalidateQueries('conversations'); 
          navigate('/messages', { state: { conversationId } });
        } else {
          showToast('Could not start or find conversation. Please try again.', 'danger');
        }
      } catch (error: any) {
        console.error('Error initiating message from UserProfilePage:', error);
        showToast(error.response?.data?.message || error.message || 'Failed to start conversation.', 'danger');
      }
    };

    // --- Connect Button Handler --- //
    const handleConnectClick = () => {
        if (profileToDisplay && profileToDisplay.id) {
            setConnectionRecipient({ id: profileToDisplay.id, name: profileToDisplay.name || 'User' });
            setShowConnectionModal(true);
        }
    };

    // --- Conditional Rendering --- 
    if (isLoading) {
        return <div className="container py-5 text-center"><LoadingSpinner text="Loading profile..." /></div>; 
    }

    if (displayError) {
        return <div className="container py-5"><Alert variant="danger">Error: {displayError}</Alert></div>;
    }

    if (!profileToDisplay) {
        return <div className="container py-5 text-center">User profile not found.</div>;
    }

    // --- Prepare Props for Children (using profileToDisplay) --- 
    const locationName = profileToDisplay.city 
        ? `${profileToDisplay.city}${profileToDisplay.country ? `, ${profileToDisplay.country}` : ''}` 
        : undefined;

    const locationProps = {
        location_name: locationName,
        coordinates: (profileToDisplay.latitude != null && profileToDisplay.longitude != null) // Check for null/undefined explicitly
            ? { lat: profileToDisplay.latitude, lng: profileToDisplay.longitude } 
            : undefined,
    };
    
    // Placeholder data for sections not yet connected to backend
    const placeholderActivities = [
        { id: 'act_ph1', icon: 'info-circle', color: 'secondary', description: 'Recent activity data not loaded yet.', time: 'Now' },
    ];
    const placeholderBadges: any[] = []; // Empty array for badges
    const placeholderConnections = 0; // Placeholder connections count

    return (
        <div className="container py-5">
             <div className="row mb-4">
                 <div className="col-12">
                     <ProfileHeader 
                        user={profileToDisplay!} // Assert not null here after checks 
                        isOwnProfile={isViewingOwnProfile} 
                        connectionStatus={connectionStatus}
                        isLoadingConnectionStatus={isLoadingConnectionStatus}
                        onConnectClick={handleConnectClick} 
                        onMessageClick={() => { // Add onMessageClick prop
                            if (profileToDisplay && profileToDisplay.id) {
                                handleInitiateMessage(profileToDisplay.id, profileToDisplay.name);
                            }
                        }}
                     /> 
                 </div>
             </div>
             <div className="row mb-4">
                 <div className="col-12">
                     <ActivityTimeline activities={placeholderActivities} />
                 </div>
             </div>

            <div className="row">
                <div className="col-lg-8 order-lg-1 order-2">
                    <ProfessionalDetails user={profileToDisplay} />
                    <AchievementBadges badges={placeholderBadges} />
                    <SkillsExpertise skills={profileToDisplay.skills || []} />
                    <NetworkingPreferences />
                </div>
                <div className="col-lg-4 order-lg-2 order-1 mb-4 mb-lg-0">
                    <LocationMap location={locationProps} />
                    <ConnectionsList connectionsCount={profileToDisplay.connectionsCount ?? 0} />
                </div>
            </div>

            {/* --- Connection Request Modal --- */}
            <Modal
                show={showConnectionModal}
                onHide={() => setShowConnectionModal(false)}
                centered
            >
                <Modal.Header closeButton>
                <Modal.Title>Connect with {connectionRecipient.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Add a personal note with your connection request:</p>
                    <Form>
                        <Form.Group>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Hi, I'd like to connect with you..."
                            value={connectionNote}
                            onChange={(e) => setConnectionNote(e.target.value)}
                        />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowConnectionModal(false)}
                        disabled={isSendingConnection}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={submitConnectionRequest}
                        disabled={isSendingConnection}
                    >
                        {isSendingConnection ? 'Sending...' : 'Send Request'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default UserProfilePage; 